/**
 * Unique Users Chart Component
 * Gráfico de linha mostrando tendência de usuários únicos
 */

import React from 'react';
import { Card, CardContent, Typography, Box, CircularProgress, Alert } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { format, parseISO } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface UniqueUsersChartProps {
  data: Array<{
    date: string;
    uniqueUsers: number;
  }>;
  loading?: boolean;
  error?: string | null;
}

export const UniqueUsersChart: React.FC<UniqueUsersChartProps> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  const chartData = {
    labels: data.map(item => format(parseISO(item.date), 'dd/MM', { locale: ptBR })),
    datasets: [
      {
        label: 'Usuários Únicos',
        data: data.map(item => item.uniqueUsers),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            return `Usuários únicos: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Número de Usuários Únicos'
        },
        ticks: {
          stepSize: 1,
          precision: 0
        }
      }
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Tendência de Usuários Únicos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Acompanhe o engajamento diário medindo quantos usuários distintos utilizaram o sistema
        </Typography>
        <Box sx={{ height: 300 }}>
          <Line data={chartData} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
};
